import type React from 'react';
import { useState, useEffect } from 'react';
import { Link as ScrollLink } from 'react-scroll';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_LINKS, COMPANY_INFO } from '../utils/constants';
import { fadeInDown, mobileMenuAnimation } from '../utils/animations';
import Button from './ui/Button';
import Icon from './ui/Icon';
import logoSvg from '../assets/images/logo.svg';

const Header: React.FC = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 20);

      if (isHomePage) {
        // Find which section is currently in view
        const sections = document.querySelectorAll('section[id]');
        const scrollPosition = window.scrollY + window.innerHeight / 2 + 10; // Center of the viewport

        for (const section of sections) {
          const sectionTop = (section as HTMLElement).offsetTop;
          const sectionHeight = (section as HTMLElement).offsetHeight;
          const sectionId = section.getAttribute('id');

          if (
            scrollPosition >= sectionTop &&
            scrollPosition < sectionTop + sectionHeight &&
            sectionId
          ) {
            setActiveSection(sectionId);
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <motion.header
      className={`fixed left-0 top-0 z-50 w-full bg-white ${
        isSticky ? 'shadow-md' : ''
      } transition-all duration-300`}
      variants={fadeInDown}
      initial="hidden"
      animate="visible"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          {isHomePage ? (
            <ScrollLink
              to="hero"
              spy={true}
              smooth={true}
              offset={-120}
              duration={500}
              className="cursor-pointer"
            >
              <div className="flex items-center">
                <img
                  src={logoSvg}
                  alt="IT Joy"
                  className="h-12 w-auto"
                />
              </div>
            </ScrollLink>
          ) : (
            <RouterLink to="/" className="cursor-pointer">
              <div className="flex items-center">
                <img
                  src={logoSvg}
                  alt="IT Joy"
                  className="h-12 w-auto"
                />
              </div>
            </RouterLink>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden md:block">
            <ul className="flex space-x-8">
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  {isHomePage ? (
                    <ScrollLink
                      to={link.to}
                      spy={true}
                      smooth={true}
                      offset={-100}
                      duration={500}
                      className={`cursor-default select-none text-sm font-medium hover:text-secondary ${
                        activeSection === link.to
                          ? 'text-secondary'
                          : 'text-gray-700'
                      }`}
                    >
                      {link.label}
                    </ScrollLink>
                  ) : (
                    <RouterLink
                      to="/"
                      className="text-sm font-medium text-gray-700 hover:text-secondary"
                    >
                      {link.label}
                    </RouterLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info & CTA (Desktop) */}
          <div className="hidden items-center space-x-4 md:flex">
            <a
              href={`tel:${COMPANY_INFO.phone}`}
              className="font-medium text-gray-800 hover:text-primary"
            >
              {COMPANY_INFO.phone}
            </a>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (isHomePage) {
                  const contactForm = document.getElementById('main-cta');
                  contactForm?.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/#main-cta';
                }
              }}
            >
              Заказать консультацию
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="text-gray-700 focus:outline-none md:hidden"
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <Icon name={mobileMenuOpen ? 'close' : 'menu'} size={28} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 top-20 z-40 bg-white p-6 shadow-lg md:hidden"
            variants={mobileMenuAnimation}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <nav className="flex flex-col space-y-6">
              <ul className="flex flex-col space-y-4">
                {NAV_LINKS.map((link) => (
                  <li key={link.id}>
                    {isHomePage ? (
                      <ScrollLink
                        to={link.to}
                        spy={true}
                        smooth={true}
                        offset={-100}
                        duration={500}
                        className="text-lg font-medium text-gray-700 hover:text-secondary"
                        onClick={closeMobileMenu}
                      >
                        {link.label}
                      </ScrollLink>
                    ) : (
                      <RouterLink
                        to="/"
                        className="text-lg font-medium text-gray-700 hover:text-secondary"
                        onClick={closeMobileMenu}
                      >
                        {link.label}
                      </RouterLink>
                    )}
                  </li>
                ))}
              </ul>

              <div className="flex flex-col space-y-4 pt-4">
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="flex items-center space-x-2 text-lg font-medium text-gray-800"
                >
                  <Icon name="phone" className="text-primary" />
                  <span>{COMPANY_INFO.phone}</span>
                </a>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="flex items-center space-x-2 text-lg font-medium text-gray-800"
                >
                  <Icon name="email" className="text-primary" />
                  <span>{COMPANY_INFO.email}</span>
                </a>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => {
                    if (isHomePage) {
                      const contactForm = document.getElementById('main-cta');
                      contactForm?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = '/#main-cta';
                    }
                    closeMobileMenu();
                  }}
                >
                  Заказать консультацию
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
